import styled from "styled-components";
import { useEffect, useState } from "react";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import DataTable from "../../Componentes/Utils/DataTable";
import SmallLoading from "../../Componentes/Utils/SmallLoading";
import useApi from "../../hooks/useApi";
import { formatCurrency } from "../../Funcoes/formatCurrency";
import { formatNumber } from "../../Funcoes/formatNumber";
import { useToast } from "../../contexts/ToastContext";

const API_URL = "Painel/API/Controladoria/PedidosCompra/PedidoFrota/index.php";

const Title = styled.h2`
    margin: 0 0 12px 0;
    font-size: 18px;
    color: var(--ctrl-text, #333);
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px 16px;
    margin-bottom: 18px;

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const InfoLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--ctrl-label, #94a3b8);
`;

const InfoValue = styled.span`
    font-size: 14px;
    color: var(--ctrl-text, #1f2937);
    font-weight: 500;
`;

const SectionTitle = styled.h3`
    margin: 18px 0 8px 0;
    font-size: 14px;
    color: var(--ctrl-text-muted, #334155);
    font-weight: 700;
`;

const TableWrapper = styled.div`
    width: 100%;
    max-height: 40dvh;
    overflow: auto;
    border: 1px solid var(--ctrl-border, #ddd);
    border-radius: 8px;
`;

const EmptyText = styled.p`
    margin: 0;
    padding: 12px;
    font-size: 14px;
    color: var(--ctrl-text-muted, #666);
`;

const LoadingWrapper = styled.div`
    width: 100%;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Coluna = styled.td`
    text-align: ${({ align }) => align || "left"};
`;

const TotalRow = styled.tr`
    background-color: var(--ctrl-surface-soft, #f8fafc);
    font-weight: 700;

    & td {
        border-top: 2px solid var(--ctrl-border-soft, #e2e8f0);
    }
`;

const ModalItensPedidoFrota = ({ pedido, onClose }) => {
    const api = useApi();
    const { notifyError } = useToast();

    const [carregando, setCarregando] = useState(true);
    const [itens, setItens] = useState([]);
    const [valorTotal, setValorTotal] = useState(0);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        let ativo = true;

        const carregarItens = async () => {
            setCarregando(true);
            setErro(null);

            try {
                const params = new URLSearchParams();
                params.append("detalhe", "1");
                params.append("filial", pedido.FILIAL);
                params.append("num_pedido", pedido.NUM_PEDIDO);

                const { data } = await api.get(`${API_URL}?${params.toString()}`);

                if (!ativo) return;

                setItens(data.data || []);
                setValorTotal(data.valor_total || 0);
            } catch (e) {
                notifyError(e?.response?.data?.message || "Erro ao carregar os itens do pedido.");
                if (ativo) setErro("Erro ao carregar os itens do pedido.");
            } finally {
                if (ativo) setCarregando(false);
            }
        };

        carregarItens();

        return () => {
            ativo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const primeiro = itens[0] || {};

    return (
        <Overlay state={true}>
            <Modal size="xl" header close={onClose}>
                <Title>Detalhes do pedido {pedido.NUM_PEDIDO}</Title>

                {carregando ? (
                    <LoadingWrapper>
                        <SmallLoading text="Carregando itens do pedido..." />
                    </LoadingWrapper>
                ) : erro ? (
                    <EmptyText>{erro}</EmptyText>
                ) : (
                    <>
                        <InfoGrid>
                            <InfoItem>
                                <InfoLabel>Pedido</InfoLabel>
                                <InfoValue>{pedido.NUM_PEDIDO || " - "}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Filial</InfoLabel>
                                <InfoValue>{pedido.FILIAL || " - "}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Fornecedor</InfoLabel>
                                <InfoValue>{primeiro.FORNECEDOR || pedido.FORNECEDOR || " - "}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Placa</InfoLabel>
                                <InfoValue>{primeiro.PLACA || pedido.PLACA || " - "}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Descrição placa</InfoLabel>
                                <InfoValue>{primeiro.DESC_PLACA || pedido.DESC_PLACA || " - "}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Observação</InfoLabel>
                                <InfoValue>{primeiro.OBS || pedido.OBS || " - "}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Itens</InfoLabel>
                                <InfoValue>{formatNumber(itens.length)}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Valor total</InfoLabel>
                                <InfoValue>{`R$ ${formatCurrency(valorTotal)}`}</InfoValue>
                            </InfoItem>
                        </InfoGrid>

                        <SectionTitle>Itens do pedido</SectionTitle>
                        {itens.length === 0 ? (
                            <EmptyText>Nenhum item encontrado neste pedido.</EmptyText>
                        ) : (
                            <TableWrapper>
                                <DataTable>
                                    <thead>
                                        <tr>
                                            <th>ITEM</th>
                                            <th>PRODUTO</th>
                                            <th>DESCRIÇÃO</th>
                                            <th>PLACA</th>
                                            <th>DESCRIÇÃO PLACA</th>
                                            <th>OBSERVAÇÃO</th>
                                            <th>QTD</th>
                                            <th>UM</th>
                                            <th>PREÇO UNIT.</th>
                                            <th>VALOR TOTAL</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itens.map((item, index) => (
                                            <tr key={`${item.ITEM}-${index}`}>
                                                <Coluna align="center">{item.ITEM || " - "}</Coluna>
                                                <td>{item.PRODUTO || " - "}</td>
                                                <td>{item.DESCRICAO || " - "}</td>
                                                <td>{item.PLACA || " - "}</td>
                                                <td>{item.DESC_PLACA || " - "}</td>
                                                <td>{item.OBS || " - "}</td>
                                                <Coluna align="right">{formatNumber(item.QUANTIDADE)}</Coluna>
                                                <Coluna align="center">{item.UNIDADE || " - "}</Coluna>
                                                <Coluna align="right">{`R$ ${formatCurrency(item.PRECO_UNIT)}`}</Coluna>
                                                <Coluna align="right">{`R$ ${formatCurrency(item.VALOR_TOTAL)}`}</Coluna>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <TotalRow>
                                            <td colSpan={9}>Valor total do pedido</td>
                                            <Coluna align="right">{`R$ ${formatCurrency(valorTotal)}`}</Coluna>
                                        </TotalRow>
                                    </tfoot>
                                </DataTable>
                            </TableWrapper>
                        )}
                    </>
                )}
            </Modal>
        </Overlay>
    );
};

export default ModalItensPedidoFrota;
